import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { CampaignProvider } from '@/types/types';
import { listCampaigns, pollCampaignsStatus } from '@/api/campaign';
import { startPolling, stopPolling } from '@/func/polling';
import translator from '@/locale/translator';

export const useProviderStore = defineStore('providers', () => {
  const providers = ref<CampaignProvider[]>([]);
  const currentOrderId = ref<string | null>(null);
  const pollingStatuses = ref<{ [key: string]: { status_string: string }[] }>({});
  const temporarySyncStatuses = ref<{ [key: string]: boolean }>({});
  const completedDecks = ref<{ [providerId: string]: Set<string> }>({}); // To track completed decks across providers by their providerId
  const loadingPublish = ref<boolean>(false);
  const obj = ref<Record<string, any>>({});

  async function fetchProviders(orderId: string) {
    await listCampaigns(orderId)
      .then((response) => {
        providers.value = response.data;
      })
      .catch((error) => {
        throw new Error('Failed to fetch providers', error);
      });
  }

  async function getProviders(orderId: string) {
    if (!providers.value.length || currentOrderId.value !== orderId) {
      currentOrderId.value = orderId;
      await fetchProviders(orderId);
    }
    return providers.value;
  }

  async function clearProviders() {
    providers.value = [];
    currentOrderId.value = null;
  }

  const updateProviderObject = (providerId: string, updatedObject: any) => {
    if (!providers.value.length) return;

    const providerIndex = providers.value.findIndex((p) => p.id === providerId);
    if (providerIndex !== -1) {
      providers.value[providerIndex] = {
        ...providers.value[providerIndex],
        ...updatedObject,
      };
    }
  };

  const updateProviderConfirmedStatus = (providerId: string, confirmed: boolean) => {
    if (!providers.value.length) return;

    const providerIndex = providers.value.findIndex((p) => p.id === providerId);
    if (providerIndex !== -1) {
      providers.value[providerIndex] = {
        ...providers.value[providerIndex],
        confirmed,
      };
    }
  };

  const providersWithUpdatedStatus = computed(() => {
    return providers.value.map((provider) => {
      // Check if this provider has a temporary sync status
      if (temporarySyncStatuses.value[provider.id]) {
        return {
          ...provider,
          provider_status: 'in_progress',
        };
      }

      // Otherwise use polling data if available
      const providerPollingData = pollingStatuses.value[provider.id];
      if (providerPollingData && Array.isArray(providerPollingData) && providerPollingData.length > 0) {
        return {
          ...provider,
          provider_status: providerPollingData[0].status_string,
        };
      }
      return provider;
    });
  });

  async function startPollingCampaigns(orderId: string) {
    startPolling(async () => {
      await pollCampaignsStatus(orderId)
        .then((response) => {
          if (response && typeof response === 'object') {
            pollingStatuses.value = { ...response };

            // Check only the latest status (first item) for each provider
            const hasActiveStatus = Object.values(response).some((campaigns: any) => {
              if (campaigns && Array.isArray(campaigns) && campaigns.length > 0) {
                const latestStatus = campaigns[0];
                return (
                  latestStatus?.status_string === 'waiting' ||
                  latestStatus?.status_string === 'blocked' ||
                  latestStatus?.status_string === 'in_progress'
                );
              }
              return false;
            });

            if (!hasActiveStatus) {
              stopPolling();
              loadingPublish.value = false;
            }
          }
        })
        .catch((error) => {
          loadingPublish.value = false;
          throw new Error('Failed to poll campaigns status', error);
        });
    });
  }

  async function stopPollingCampaigns() {
    stopPolling();
  }

  const addCompletedCard = (cardKey: string, providerId: string) => {
    if (!providerId) return;

    const currentDecks = { ...completedDecks.value };
    if (!currentDecks[providerId]) {
      currentDecks[providerId] = new Set<string>();
    }
    currentDecks[providerId] = new Set([...currentDecks[providerId], cardKey]);
    completedDecks.value = currentDecks;
  };

  // LinkedIn needs a T&C consent card on top of its field groups; the backend doesn't track it
  const isLinkedInTermsPending = (provider: CampaignProvider) =>
    provider.provider === 'linkedin' &&
    !provider.confirmed &&
    !completedDecks.value[provider.id]?.has(translator.get('campaigns.linkedin.terms_and_conditions'));

  // backend `values_valid` plus any consent the backend can't track
  const isProviderReady = (provider: CampaignProvider) => provider.values_valid && !isLinkedInTermsPending(provider);

  // true only when every provider has a completed card for each of its field groups, which lets
  // the UI tell "something is still missing" apart from "everything is saved but still blocked"
  const allProvidersCardsCompleted = computed(() => {
    if (!providers.value.length) return false;
    return providers.value.every((provider) => {
      if (!provider.groups?.length) return false;
      if (isLinkedInTermsPending(provider)) return false;
      return (completedDecks.value[provider.id]?.size ?? 0) >= provider.groups.length;
    });
  });

  const clearCompletedCards = (providerId: string) => {
    if (!providerId) return;
    completedDecks.value = {
      ...completedDecks.value,
      [providerId]: new Set<string>(),
    };
  };

  const clearCompletedDecks = () => {
    completedDecks.value = {};
  };

  // `obj` holds the provider values currently being edited and is shared by every provider form.
  // it has to be dropped whenever the edited campaign changes, otherwise keys the next form does
  // not itself write — another campaign's banner selection, target locations, ads — stay behind and
  // are sent along with its preview requests.
  const resetProviderValues = () => {
    obj.value = {};
  };

  return {
    providers,
    pollingStatuses,
    providersWithUpdatedStatus,
    currentOrderId,
    completedDecks,
    allProvidersCardsCompleted,
    isProviderReady,
    loadingPublish,
    obj,
    updateProviderObject,
    updateProviderConfirmedStatus,
    getProviders,
    clearProviders,
    startPollingCampaigns,
    stopPollingCampaigns,
    addCompletedCard,
    clearCompletedCards,
    clearCompletedDecks,
    resetProviderValues,
  };
});
